import { faAdd, faRemove } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type {
  GoogleAdsSmartCampaign,
  IGoogleAdsKeyword,
  IGoogleAdsSuggestedKeywordThemes,
} from '@feathr/blackbox';
import {
  Button,
  CardContent,
  CardHeader,
  CardV2 as Card,
  Form,
  Input,
  Label,
  Spinner,
  toast,
} from '@feathr/components';
import type { TValidateGrouped } from '@feathr/rachis';
import { validate } from '@feathr/rachis';

import KeywordButton from './KeywordButton';

import * as styles from './KeywordsStep.css';

interface IKeywordsStepProps {
  readonly campaign: GoogleAdsSmartCampaign;
}

export function validateKeywordsStep(campaign: GoogleAdsSmartCampaign): TValidateGrouped {
  return campaign.validate(['keyword_themes'], false, 'grouped').errors;
}

const LEARN_MORE_LINK = 'https://support.google.com/google-ads/answer/9263830';

function KeywordsStep({ campaign }: IKeywordsStepProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedKeywords, setSelectedKeywords] = useState<IGoogleAdsKeyword[]>(
    campaign.get('keyword_themes') ?? [],
  );
  const [suggestedKeywords, setSuggestedKeywords] = useState<IGoogleAdsKeyword[]>([]);
  const [customKeyword, setCustomKeyword] = useState<string>('');
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);

  const flattenedSelectedKeywords = selectedKeywords.map((keyword) => keyword.display_name);

  // Suggest keywords on the first render of the page
  useEffect(() => {
    setIsLoadingSuggestions(true);
    campaign
      .suggest<IGoogleAdsSuggestedKeywordThemes>({
        type: 'keyword',
        businessName: campaign.get('business_name'),
        destinationUrl: campaign.get('destination_url'),
      })
      .then((keywords) => {
        // Filter out any selected keywords from the suggested keywords
        const suggestedKeywordThemes = keywords.keyword_themes.filter(
          (suggestedKeyword: IGoogleAdsKeyword): boolean =>
            !flattenedSelectedKeywords.includes(suggestedKeyword.display_name),
        );

        setSuggestedKeywords(suggestedKeywordThemes);
      })
      .catch((error) => {
        toast(t('Something went wrong: {{-error}}', { error }), {
          type: ToastType.ERROR,
        });
      })
      .finally(() => {
        setIsLoadingSuggestions(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSelectKeyword(keyword: IGoogleAdsKeyword): void {
    const newSelectedKeywords = [...selectedKeywords, keyword];
    setSelectedKeywords(newSelectedKeywords);
    campaign.set({ keyword_themes: newSelectedKeywords });

    // Filter out selected keyword from suggested keywords
    updateSuggestedKeywords(keyword);
  }

  // Filter out selected keyword from suggested keywords
  function updateSuggestedKeywords(keyword: IGoogleAdsKeyword): void {
    setSuggestedKeywords(
      suggestedKeywords.filter(
        (suggestedKeyword) => suggestedKeyword.display_name !== keyword.display_name,
      ),
    );
  }

  function handleRemoveKeyword(keyword: IGoogleAdsKeyword): void {
    const newSelectedKeywords = selectedKeywords.filter(
      (selectedKeyword) => selectedKeyword.display_name !== keyword.display_name,
    );
    setSelectedKeywords(newSelectedKeywords);
    campaign.set({ keyword_themes: newSelectedKeywords });

    // Add the keyword back to the suggested keywords if it was there to begin with
    if (keyword.suggestion) {
      setSuggestedKeywords([...suggestedKeywords, keyword]);
    }
  }

  function handleAddKeywordTheme(): void {
    const keyword = { display_name: customKeyword, suggestion: false };

    // See if the user submitted keyword is already in the suggested keywords
    const customKeywordAsSuggestion = suggestedKeywords.find(
      (suggestedKeyword) => suggestedKeyword.display_name === customKeyword,
    );

    // Add new user input keyword to selected keyword if not already present
    if (!flattenedSelectedKeywords.includes(keyword.display_name)) {
      const keywords = [...selectedKeywords, customKeywordAsSuggestion ?? keyword];
      setSelectedKeywords(keywords);
      campaign.set({ keyword_themes: keywords });
      if (customKeywordAsSuggestion) {
        updateSuggestedKeywords(customKeywordAsSuggestion);
      }
    }

    // Clear the input
    setCustomKeyword('');
  }

  function handleOnChangeCustomKeyword(value?: string): void {
    // If there's no value provide an empty string the user can erase the input
    if (value) {
      setCustomKeyword(value);
    } else {
      setCustomKeyword('');
    }
  }

  function validateKeyword(): string | undefined {
    const error: Array<string | undefined> = validate.single(customKeyword, {
      presence: {
        allowEmpty: false,
        message: t("Can't be blank"),
      },
      format: {
        // Allow letters, numbers, spaces, and hyphens
        pattern: /^[a-zA-Z0-9 -]{3,}$/,
        message: t('Enter short words or phrases without punctuation'),
      },
    });
    return error ? error[0] : undefined;
  }

  const suggestedKeywordButtons = suggestedKeywords.length ? (
    suggestedKeywords.map((keyword) => (
      <KeywordButton
        key={keyword.resource_name ?? keyword.display_name}
        keyword={keyword}
        onClick={handleSelectKeyword}
        suffix={<FontAwesomeIcon icon={faAdd} />}
      />
    ))
  ) : (
    <span>{t('No keywords available')}</span>
  );

  const selectedKeywordButtons = selectedKeywords.length ? (
    selectedKeywords.map((keyword) => (
      <KeywordButton
        key={keyword.resource_name ?? keyword.display_name}
        keyword={keyword}
        onClick={handleRemoveKeyword}
        suffix={<FontAwesomeIcon icon={faRemove} />}
      />
    ))
  ) : (
    <span>{t('No keywords selected')}</span>
  );

  return (
    <div className={styles.root}>
      <Form label={t('Keywords')}>
        <Card name={'keywords'}>
          <CardHeader
            description={
              <Trans t={t}>
                Give us a few keyword themes and we'll show your ad for similar searches. For best
                results, limit to 7-10 keyword themes.{' '}
                <a href={LEARN_MORE_LINK} target={'_blank'}>
                  Learn more
                </a>
              </Trans>
            }
            title={t('Keyword Themes')}
          />
          <CardContent addVerticalGap={true}>
            <div>
              <Label>{t('Suggested keyword themes')}</Label>
              <div data-name={'suggestedKeywords'}>
                {isLoadingSuggestions ? <Spinner /> : suggestedKeywordButtons}
              </div>
            </div>
            <div>
              <Label>{t('Selected keyword themes')}</Label>
              <div data-name={'selectedKeywords'}>
                {campaign?.isPending ? <Spinner /> : selectedKeywordButtons}
              </div>
            </div>
            <div className={styles.customKeywordInputWrapper}>
              <Input
                aria-label={'Add custom keyword'}
                className={styles.input}
                onChange={handleOnChangeCustomKeyword}
                placeholder={t('Keyword theme')}
                // Only show the error if the user has started typing
                validationError={customKeyword ? validateKeyword() : undefined}
                value={customKeyword}
                wrapperClassName={styles.inputWrapper}
              />
              <Button
                disabled={!!validateKeyword()}
                name={'add_keyword_theme'}
                onClick={handleAddKeywordTheme}
                prefix={<FontAwesomeIcon icon={faPlus} />}
                tooltip={validateKeyword()}
                type={'icon-outlined'}
              >
                {t('Add')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </Form>
    </div>
  );
}

export default observer(KeywordsStep);
